import { useState, useRef, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ScanLine, Loader2, Sparkles, Trash2, Check, PackagePlus } from "lucide-react";

export default function BulkScanDialog({ open, onClose, onCreated }) {
  const [rows, setRows] = useState([]);
  const [code, setCode] = useState("");
  const [auto, setAuto] = useState(true);
  const [creating, setCreating] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setRows([]); setCode(""); setTimeout(() => inputRef.current?.focus(), 200); }
  }, [open]);

  const patchBc = (bc, p) => setRows((r) => r.map((x) => (x.barcode === bc ? { ...x, ...p } : x)));
  const removeBc = (bc) => setRows((r) => r.filter((x) => x.barcode !== bc));

  const suggest = async (bc, hint) => {
    patchBc(bc, { loading: true });
    try {
      const { data } = await api.post("/products/barcode-lookup", { barcode: bc, hint: hint || "" });
      if (data.found) patchBc(bc, { loading: false, status: "exists", name: data.product.name || "" });
      else if (data.suggestion) patchBc(bc, { loading: false, status: "ready", name: data.suggestion.name || "", brand: data.suggestion.brand || "" });
      else patchBc(bc, { loading: false, status: "manual" });
    } catch { patchBc(bc, { loading: false, status: "manual" }); }
  };

  const addBarcode = (raw) => {
    const bc = String(raw || "").replace(/\s/g, "").trim();
    setCode("");
    if (!bc) return;
    if (rows.some((x) => x.barcode === bc)) { toast.info("Bu barkod listede zaten var"); return; }
    setRows((r) => [...r, { barcode: bc, name: "", brand: "", no_brand: false, status: "scanned", loading: false }]);
    if (auto) setTimeout(() => suggest(bc), 0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const suggestAll = async () => {
    setSuggesting(true);
    for (const x of rows.filter((r) => r.status !== "exists" && !(r.name || "").trim())) {
      await suggest(x.barcode);
    }
    setSuggesting(false);
  };

  const create = async () => {
    const items = rows
      .filter((x) => x.status !== "exists" && (x.name || "").trim())
      .map((x) => ({ barcode: x.barcode, name: x.name.trim(), brand: x.no_brand ? "" : (x.brand || "").trim(), no_brand: !!x.no_brand, base_unit: "adet" }));
    if (!items.length) return toast.error("Adı girilmiş (oluşturulacak) ürün yok");
    setCreating(true);
    try {
      const { data } = await api.post("/products/bulk-create", { items });
      toast.success(`${data.created_count} ürün oluşturuldu${data.skipped_count ? `, ${data.skipped_count} atlandı` : ""}`);
      const createdBc = new Set((data.created || []).map((c) => c.barcode));
      setRows((r) => r.filter((x) => !createdBc.has(x.barcode)));
      onCreated?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Toplu oluşturma başarısız"); }
    finally { setCreating(false); }
  };

  const readyCount = rows.filter((x) => x.status !== "exists" && (x.name || "").trim()).length;
  const badge = (s) => ({
    scanned: <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">Okundu</span>,
    ready: <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">Öneri hazır</span>,
    manual: <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Elle gir</span>,
    exists: <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Zaten kayıtlı</span>,
  }[s] || null);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ScanLine className="h-5 w-5 text-sky-600" /> Toplu Barkod Tarama
          </DialogTitle>
          <DialogDescription>El terminaliyle barkodları art arda okutun. Her barkod için ürün adı otomatik önerilir (marka + miktar + birim); kontrol edip topluca oluşturun.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <ScanLine className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input ref={inputRef} data-testid="bulk-scan-input" value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBarcode(code); } }}
              placeholder="Barkodu okutun veya yazıp Enter'a basın" className="pl-9 font-mono" />
          </div>
          <Button type="button" variant="outline" data-testid="bulk-scan-add" onClick={() => addBarcode(code)}>Ekle</Button>
          <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
            <Switch data-testid="bulk-scan-auto" checked={auto} onCheckedChange={setAuto} /> Otomatik AI öner
          </label>
        </div>

        <div className="max-h-[52vh] overflow-y-auto rounded-lg ring-1 ring-inset ring-slate-100">
          {rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400" data-testid="bulk-scan-empty">Henüz barkod okutulmadı.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr><th className="px-3 py-2">Barkod</th><th className="px-3 py-2">Ürün Adı</th><th className="px-3 py-2">Marka</th><th className="px-3 py-2">Durum</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {rows.map((x) => (
                  <tr key={x.barcode} data-testid={`bulk-scan-row-${x.barcode}`} className="border-t border-slate-50 align-top">
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{x.barcode}</td>
                    <td className="px-3 py-2">
                      <input data-testid={`bulk-scan-name-${x.barcode}`} value={x.name || ""} disabled={x.status === "exists"}
                        onChange={(e) => patchBc(x.barcode, { name: e.target.value })}
                        placeholder="Ürün Adı Marka Miktar Birim" className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs disabled:bg-slate-100" />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <input data-testid={`bulk-scan-brand-${x.barcode}`} value={x.brand || ""} disabled={x.no_brand || x.status === "exists"}
                          onChange={(e) => patchBc(x.barcode, { brand: e.target.value })} placeholder="Marka"
                          className="h-8 w-24 rounded-md border border-slate-200 px-2 text-xs disabled:bg-slate-100" />
                        <label className="flex items-center gap-1 text-[10px] text-slate-500">
                          <input type="checkbox" data-testid={`bulk-scan-nobrand-${x.barcode}`} checked={!!x.no_brand}
                            onChange={(e) => patchBc(x.barcode, { no_brand: e.target.checked })} className="h-3.5 w-3.5" /> Markasız
                        </label>
                      </div>
                    </td>
                    <td className="px-3 py-2">{x.loading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : badge(x.status)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {x.status !== "exists" && (
                          <button data-testid={`bulk-scan-ai-${x.barcode}`} onClick={() => suggest(x.barcode, x.name)} title="AI ile ad öner"
                            className="rounded p-1 text-brand-orange hover:bg-orange-50"><Sparkles className="h-4 w-4" /></button>
                        )}
                        <button data-testid={`bulk-scan-remove-${x.barcode}`} onClick={() => removeBc(x.barcode)} title="Kaldır"
                          className="rounded p-1 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <span className="text-xs text-slate-500" data-testid="bulk-scan-count">{rows.length} okundu · <b className="text-brand-green">{readyCount}</b> oluşturulacak</span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" data-testid="bulk-scan-suggest-all" onClick={suggestAll} disabled={suggesting || rows.length === 0}>
              {suggesting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-brand-orange" />} Tümüne AI Öner
            </Button>
            <Button type="button" data-testid="bulk-scan-create" onClick={create} disabled={creating || readyCount === 0} className="bg-brand-green text-white hover:bg-emerald-600">
              {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PackagePlus className="mr-1.5 h-4 w-4" />} Oluştur ({readyCount})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
